import React, { useState, useEffect, useRef } from 'react'
import { Tabs, Tab, Button, Card, Badge } from 'react-bootstrap'
import { toast } from 'react-toastify'
import formatCpf from '@brazilian-utils/format-cpf'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'

import ortodontiaService from '../../../services/ortodontia.service'
import PacienteService from '../../../services/paciente.service'
import MenuContext from '../../../contexts/MenuContexts'
import PageLoading from '../../Common/PageLoading'
import NegativacaoStatusBadge from '../../Common/NegativacaoStatusBadge'
import PacienteForm from './PacienteForm'
import PacientePlanosTratamento from './planoTratamento'
import PacientePlanosOrtodontia from './planoTratamentoOrtodontico'
import AvaliacoesPendentes from './avaliacoes'
import PagamentosRealizados from './pagamentos'
import HistoricoAgendamentos from './agendamentos'
import HistoricoMensagens from './historicoMensagens'
import TabDocumentos from './documentos/TabDocumentos'

function getInitials(nome) {
  if (!nome || typeof nome !== 'string') return '•'
  const parts = nome.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '•'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

function formatCpfSafe(cpf) {
  if (!cpf) return '—'
  try {
    return formatCpf(String(cpf).replace(/\D/g, ''))
  } catch {
    return cpf
  }
}

const PacienteDetails = (props) => {
  const acoesPermitidas = {}
  const TabsConstants = {
    dados: 'Dados Pessoais',
    planos: 'Planos de Tratamento',
    planosOrtodontia: 'Planos Ortodônticos',
    pagamentos: 'Pagamentos',
    avaliacoesPendentes: 'Avaliações Pendentes',
    agendamento: 'Agendamentos',
    mensagens: 'Mensagens',
    documentos: 'Documentos',
  }

  const { id } = props.match.params
  const isCadastro = props.location && props.location.pathname && props.location.pathname.includes('/pacientes/cadastrar')
  const pacienteId = !isCadastro ? props.pacienteId || id : null

  const { pacienteNome, cpfPaciente, pacienteTelefones, agendamentoId, nomeColaborador } = props
  const INITIAL_PACIENTE = { nome: pacienteNome, cpf: cpfPaciente, telefones: pacienteTelefones, agendamentoId }

  const [isLoading, setIsLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useState(TabsConstants.dados)
  const [paciente, setPaciente] = useState({ ...INITIAL_PACIENTE })
  const [planosTratamentoList, setPlanosTratamentoList] = useState([])
  const [planosTratamentoOrtodontiaList, setPlanosTratamentoOrtodontiaList] = useState([])
  const [avaliacoesPendentesList, setAvaliacoesPendentesList] = useState([])
  const [pagamentosRealizados, setPagamentosRealizados] = useState([])

  const hasCleanedRef = useRef(false)

  useEffect(() => {
    if (isCadastro && !hasCleanedRef.current) {
      setPaciente({ ...INITIAL_PACIENTE })
      setPlanosTratamentoList([])
      setPlanosTratamentoOrtodontiaList([])
      setAvaliacoesPendentesList([])
      setPagamentosRealizados([])
      setSelectedTab(TabsConstants.dados)
      hasCleanedRef.current = true
      setIsLoading(false)
      return
    }

    didMount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pacienteId, isCadastro])

  const didMount = async () => {
    if (pacienteId) {
      setIsLoading(true)
      await Promise.all([
        getPaciente(),
        getPlanosTratamentoList(),
        getPlanosTratamentoOrtodontiaList(),
        getAvaliacoesPendentesList(),
        getPagamentosRealizados(),
      ])
      setIsLoading(false)
    }
  }

  const getPaciente = async () => {
    try {
      const { data } = await PacienteService.getPacienteById(pacienteId)
      setPaciente(data)
    } catch (e) {}
  }

  const getPlanosTratamentoList = async () => {
    try {
      const trazerDependentes = true
      const { data } = await PacienteService.getPlanosDeTratamento(pacienteId, trazerDependentes)
      setPlanosTratamentoList(data)
    } catch (e) {}
  }

  const getPlanosTratamentoOrtodontiaList = async () => {
    try {
      const { data } = await ortodontiaService.getPlanosTratamentoByPaciente(pacienteId)
      setPlanosTratamentoOrtodontiaList(data)
    } catch (e) {}
  }

  const getAvaliacoesPendentesList = async () => {
    try {
      const { data } = await PacienteService.getAllAvaliacoesPendentes(pacienteId)
      setAvaliacoesPendentesList(data)
      if (data.length === 0) {
        setSelectedTab(TabsConstants.dados)
      }
    } catch (e) {}
  }

  const getPagamentosRealizados = async () => {
    try {
      const { data } = await PacienteService.getPagamentos(pacienteId)
      setPagamentosRealizados(data)
    } catch (e) {}
  }

  const refreshPlanosTratamentoOrtodontiaList = async () => {
    setIsLoading(true)
    try {
      const { data } = await ortodontiaService.getPlanosTratamentoByPaciente(pacienteId)
      setPlanosTratamentoOrtodontiaList(data)
    } catch (e) {}
    setIsLoading(false)
  }

  const salvarPaciente = async (editedPaciente) => {
    setIsLoading(true)
    try {
      if (!editedPaciente.id) {
        const { data } = await PacienteService.criarPaciente(
          editedPaciente,
          editedPaciente.enderecos[0].comprovanteResidencia
        )
        toast('Paciente salvo com sucesso.', { type: toast.TYPE.SUCCESS })
        const novoId = data && (data.id || data.Id)
        if (novoId) {
          props.history.replace(`/pacientes/${novoId}`)
        } else {
          props.history.push('/pacientes')
        }
      } else {
        await PacienteService.salvarPaciente(editedPaciente, editedPaciente.enderecos[0].comprovanteResidencia)
        toast('Paciente salvo com sucesso.', { type: toast.TYPE.SUCCESS })
        props.history.push('/pacientes')
      }
    } catch (error) {}
    setIsLoading(false)
  }

  const handleSelectedTab = async (tab) => {
    setIsLoading(true)
    if (tab === TabsConstants.pagamentos) {
      await getPagamentosRealizados()
    }
    setSelectedTab(tab)
    setIsLoading(false)
  }

  const voltarLista = () => props.history.push('/pacientes')

  const renderLoading = () => <PageLoading />

  const renderPatientIdentityCard = (bodyClassName = 'py-2 px-3') => (
    <Card className="border-0 shadow-sm overflow-hidden paciente-detalhes-identity-card" style={{ borderRadius: 14 }}>
      <Card.Body className={bodyClassName}>
        <div className="d-flex align-items-center">
          <div
            className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle text-white shadow-sm paciente-detalhes-identity-avatar"
            style={{
              width: 56,
              height: 56,
              fontSize: '1.25rem',
              fontWeight: 600,
              background: 'linear-gradient(145deg, #0881c7 0%, #06659e 100%)',
            }}
          >
            {getInitials(paciente.nome)}
          </div>
          <div className="ml-3 flex-grow-1 min-w-0">
            <h2 className="h5 mb-1 text-dark text-break paciente-detalhes-identity-nome" style={{ fontWeight: 500 }}>
              {paciente.nome || 'Carregando…'}
            </h2>
            <p
              className="small text-muted mb-2 text-truncate mb-md-1 d-flex align-items-center flex-wrap"
              title={paciente.cpf ? formatCpfSafe(paciente.cpf) : ''}
            >
              <span>{paciente.cpf ? formatCpfSafe(paciente.cpf) : '—'}</span>
              <NegativacaoStatusBadge status={paciente.statusNegativacaoAtivo} className="ml-2" />
            </p>
            {typeof paciente.ativo === 'boolean' && (
              <Badge pill variant={paciente.ativo ? 'success' : 'secondary'} className="font-weight-normal">
                {paciente.ativo ? 'Ativo' : 'Inativo'}
              </Badge>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  )

  const renderMobileHero = () => {
    if (!pacienteId || isCadastro) return null
    return (
      <div className="d-md-none mb-3 paciente-detalhes-mobile-hero">
        <Button
          variant="link"
          className="text-secondary pl-0 mb-2 d-flex align-items-center p-0"
          onClick={voltarLista}
          style={{ fontSize: '0.95rem' }}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
          Voltar para pacientes
        </Button>
        {renderPatientIdentityCard('py-2 px-3')}
      </div>
    )
  }

  const renderDesktopPatientHeader = () => {
    if (!pacienteId || isCadastro) return null
    return (
      <header className="d-none d-md-block paciente-detalhes-desktop-hero pb-3 border-bottom">
        <div className="d-flex align-items-center min-w-0">
          <div
            className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center paciente-detalhes-desktop-avatar"
            aria-hidden="true"
          >
            {getInitials(paciente.nome)}
          </div>
          <div className="ml-3 min-w-0 flex-grow-1">
            <div className="paciente-detalhes-desktop-nome text-break">{paciente.nome || 'Carregando…'}</div>
            <div className="paciente-detalhes-desktop-cpf text-muted small mt-0 d-flex align-items-center flex-wrap">
              <span>{paciente.cpf ? formatCpfSafe(paciente.cpf) : '—'}</span>
              <NegativacaoStatusBadge status={paciente.statusNegativacaoAtivo} className="ml-2" />
            </div>
            {typeof paciente.ativo === 'boolean' && (
              <Badge
                pill
                variant="light"
                className="paciente-detalhes-desktop-badge mt-1 font-weight-normal border"
              >
                {paciente.ativo ? 'Ativo' : 'Inativo'}
              </Badge>
            )}
          </div>
        </div>
      </header>
    )
  }

  const renderTabs = () => {
    const PacienteFormProps = {
      paciente,
      salvar: salvarPaciente,
      handleCancel: voltarLista,
      refreshPaciente: pacienteId ? getPaciente : undefined,
    }
    if (!pacienteId) {
      return <PacienteForm {...PacienteFormProps} />
    }
    return (
      <Tabs
        id="paciente-detalhes-tabs"
        activeKey={selectedTab}
        onSelect={(tab) => handleSelectedTab(tab)}
        variant="tabs"
        className="paciente-detalhes-tabs"
      >
        <Tab eventKey={TabsConstants.dados} title={TabsConstants.dados}>
          <PacienteForm {...PacienteFormProps} />
        </Tab>
        <Tab eventKey={TabsConstants.planos} title={TabsConstants.planos}>
          <PacientePlanosTratamento
            paciente={paciente}
            nomeColaborador={nomeColaborador}
            planosTratamentoList={planosTratamentoList}
            getPlanosTratamentoList={getPlanosTratamentoList}
            getPagamentosRealizados={getPagamentosRealizados}
          />
        </Tab>
        <Tab eventKey={TabsConstants.planosOrtodontia} title={TabsConstants.planosOrtodontia}>
          <PacientePlanosOrtodontia
            paciente={paciente}
            nomeColaborador={nomeColaborador}
            planosTratamentoList={planosTratamentoOrtodontiaList}
            refreshPlanosTratamentoOrtodontiaList={refreshPlanosTratamentoOrtodontiaList}
            getPagamentosRealizados={getPagamentosRealizados}
          />
        </Tab>
        <Tab eventKey={TabsConstants.pagamentos} title={TabsConstants.pagamentos}>
          <PagamentosRealizados
            setIsLoading={setIsLoading}
            pagamentosRealizados={pagamentosRealizados}
            paciente={paciente}
            getPagamentosRealizados={getPagamentosRealizados}
          />
        </Tab>
        <Tab eventKey={TabsConstants.agendamento} title={TabsConstants.agendamento}>
          <HistoricoAgendamentos setIsLoading={setIsLoading} pacienteId={pacienteId} />
        </Tab>
        <Tab eventKey={TabsConstants.mensagens} title={TabsConstants.mensagens}>
          <HistoricoMensagens pacienteId={pacienteId} />
        </Tab>
        {avaliacoesPendentesList.length > 0 && (
          <Tab eventKey={TabsConstants.avaliacoesPendentes} title={TabsConstants.avaliacoesPendentes}>
            <AvaliacoesPendentes
              setIsLoading={setIsLoading}
              avaliacoesPendentesList={avaliacoesPendentesList}
              getAvaliacoesPendentesList={getAvaliacoesPendentesList}
              paciente={paciente}
              {...props}
            />
          </Tab>
        )}
        <Tab eventKey={TabsConstants.documentos} title={TabsConstants.documentos}>
          <TabDocumentos
            setIsLoading={setIsLoading}
            pacienteId={pacienteId}
            planosOrtodontia={planosTratamentoOrtodontiaList}
            planosTratamento={planosTratamentoList}
          />
        </Tab>
      </Tabs>
    )
  }

  return (
    <MenuContext.Consumer>
      {(acoes) => {
        acoes.forEach((a) => {
          acoesPermitidas[`${a.nome}`] = a
        })
        return (
          <div className="paciente-detalhes-page">
            <style>
              {`
                .paciente-detalhes-page .paciente-detalhes-tabs .nav-tabs {
                  flex-wrap: nowrap;
                  overflow-x: auto;
                  overflow-y: hidden;
                  -webkit-overflow-scrolling: touch;
                  scrollbar-width: thin;
                  border-bottom: 1px solid #dee2e6;
                  gap: 0;
                }
                .paciente-detalhes-page .paciente-detalhes-tabs .nav-tabs .nav-item {
                  margin-bottom: -1px;
                  flex-shrink: 0;
                }
                .paciente-detalhes-page .paciente-detalhes-tabs .nav-tabs .nav-link {
                  border: 1px solid transparent;
                  border-top-left-radius: 0.35rem;
                  border-top-right-radius: 0.35rem;
                  padding: 0.55rem 0.85rem;
                  font-size: 0.875rem;
                  font-weight: 500;
                  color: #495057;
                  background: transparent;
                  white-space: nowrap;
                }
                .paciente-detalhes-page .paciente-detalhes-tabs .nav-tabs .nav-link:hover {
                  border-color: #e9ecef #e9ecef #dee2e6;
                  color: #0881c7;
                }
                .paciente-detalhes-page .paciente-detalhes-tabs .nav-tabs .nav-link.active {
                  color: #0881c7;
                  background-color: #fff;
                  border-color: #dee2e6 #dee2e6 #fff;
                }
                .paciente-detalhes-page .paciente-detalhes-tabs .tab-content {
                  padding-top: 1rem;
                }
                @media (min-width: 768px) {
                  .paciente-detalhes-page .paciente-detalhes-tabs .nav-tabs .nav-link {
                    padding: 0.65rem 1rem;
                    font-size: 0.9rem;
                  }
                  .paciente-detalhes-desktop-hero {
                    border-color: #e9ecef !important;
                  }
                  .paciente-detalhes-desktop-avatar {
                    width: 40px;
                    height: 40px;
                    font-size: 0.8125rem;
                    font-weight: 600;
                    letter-spacing: 0.02em;
                    color: #06659e;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                  }
                  .paciente-detalhes-desktop-nome {
                    font-size: 1.0625rem;
                    font-weight: 600;
                    color: #334155;
                    line-height: 1.35;
                  }
                  .paciente-detalhes-desktop-cpf {
                    font-size: 0.8125rem;
                    letter-spacing: 0.01em;
                  }
                  .paciente-detalhes-desktop-badge {
                    font-size: 0.65rem;
                    padding: 0.2em 0.55em;
                    color: #64748b !important;
                    border-color: #e2e8f0 !important;
                    background: #fafafa !important;
                  }
                }
                @media (max-width: 767.98px) {
                  .paciente-detalhes-page {
                    padding-left: 2px;
                    padding-right: 2px;
                  }
                  .paciente-detalhes-page .paciente-detalhes-tabs .tab-content {
                    padding-top: 0.85rem;
                  }
                }
              `}
            </style>

            {renderMobileHero()}
            {renderDesktopPatientHeader()}

            <h1
              className={`h3 mb-4 text-secondary font-weight-normal ${pacienteId && !isCadastro ? 'd-none' : ''}`}
            >
              {pacienteId ? 'Detalhes do' : 'Cadastrar'} Paciente
            </h1>

            {isCadastro && (
              <p className="d-md-none text-muted small mb-3">
                Preencha os dados abaixo para cadastrar um novo paciente.
              </p>
            )}

            {renderTabs()}
            {isLoading && renderLoading()}
          </div>
        )
      }}
    </MenuContext.Consumer>
  )
}

export default PacienteDetails
